import {parsePhoneNumberFromString} from 'libphonenumber-js/max';

import type {ValidationFunc} from 'Shared/formHelperTypes';

/**
 * A validation function that checks if a given input value is not empty, undefined, or NaN.
 * It's designed to be used as a generic validation rule for various form inputs, ensuring that a field has been filled in or selected by the user.
 * The function handles different types of input, including strings, arrays, and numbers, to accommodate diverse form fields like text inputs, select options, and numeric inputs.
 *
 * @param value The value of the form field to be validated. Can be of type string, array, number, or undefined.
 * @returns Either an array containing a string message 'Bitte prüfen' if the validation fails, or null if the validation passes.
 *
 * @example
 * ```tsx
 * const validationResult = required(''); // Returns ['Bitte prüfen']
 * const validationResult2 = required('text'); // Returns null
 * ```
 */
export const required: ValidationFunc = value => {
    if (
        (typeof value === 'string' && value.trim() === '')
        || (Array.isArray(value) && value.length === 0)
        || typeof value === 'undefined'
        || (typeof value === 'number' && Number.isNaN(value))
        || (value === false)
    ) {
        return ['Bitte ausfüllen'];
    }

    return null;
};

/**
 * Creates a validation function that checks if the length of a string falls within a specified minimum and maximum range.
 * This validator is particularly useful for enforcing character length constraints on text inputs, such as usernames or passwords, ensuring that the input value adheres to defined limits.
 * The function first checks if the input is of type string, then validates its length against the provided minimum and maximum values.
 *
 * @returns A `ValidationFunc` that takes a string value as input and returns an error message array if the value's length is outside the specified range, or null if the value passes the validation.
 *
 * @example
 * ```tsx
 * const validateLength = minMaxLength(5, 10);
 * const validationResult = validateLength('hello world'); // Returns ['Dieses Feld muss zwischen 5 und 10 Zeichen lang sein']
 * const validationResult2 = validateLength('hello'); // Returns null
 * ```
 */
const MemoizedMinMax = () => {
    const cache = new Map<string, ValidationFunc>();

    return (min: number, max: number): ValidationFunc => {
        if (cache.has(`${min}:${max}`)) {
            return cache.get(`${min}:${max}`)!;
        }

        cache.set(`${min}:${max}`, value => {
            if (typeof value === 'string' && (value.length < min || value.length > max)) {
                return [`Dieses Feld muss zwischen ${min} und ${max} Zeichen lang sein`];
            }

            return null;
        });

        return cache.get(`${min}:${max}`)!;
    };
};

/**
 * Creates a validation function that checks if the length of a string falls within a specified minimum and maximum range.
 * This validator is particularly useful for enforcing character length constraints on text inputs, such as usernames or passwords, ensuring that the input value adheres to defined limits.
 * The function first checks if the input is of type string, then validates its length against the provided minimum and maximum values.
 *
 * @param min The minimum length allowed for the input value.
 * @param max The maximum length allowed for the input value.
 * @returns A `ValidationFunc` that takes a string value as input and returns an error message array if the value's length is outside the specified range, or null if the value passes the validation.
 *
 * @example
 * ```tsx
 * const validateLength = minMaxLength(5, 10);
 * const validationResult = validateLength('hello world'); // Returns ['Dieses Feld muss zwischen 5 und 10 Zeichen lang sein']
 * const validationResult2 = validateLength('hello'); // Returns null
 * ```
 */
export const minMaxLength = MemoizedMinMax();

/**
 * Validates that a field contains only numeric characters. This validation function checks if the provided value
 * consists solely of digits (0-9) without any other characters. It is particularly useful for validating input fields
 * that should accept only numeric values, such as phone numbers, zip codes, or numeric identifiers.
 * If the check fails (i.e., the value contains non-numeric characters), an error message is returned.
 *
 * @param value The value of the form field to be validated. Expected to be a string for the validation to proceed.
 * @returns If the value is a string and contains only digits, `null` is returned, indicating a pass on the validation. If the value includes non-numeric characters, an array containing a descriptive error message is returned.
 *
 * @example
 * ```tsx
 * const error = onlyNumbers('12345'); // Returns null, validation passes
 * const errorWithMessage = onlyNumbers('12a45'); // Returns ['Dieses Feld darf nur Zahlen enthalten']
 * ```
 */
export const onlyNumbers: ValidationFunc = value => {
    if (typeof value === 'string' && !(/^\d+$/u).test(value)) {
        return ['Dieses Feld darf nur Zahlen enthalten'];
    }

    return null;
};

/**
 * A validation function `validEmail` that checks if the provided email address conforms to a valid format.
 * This function uses a regular expression to validate general email structures and includes additional logic
 * for development environments where only specific domains are considered valid.
 * The function returns null if the email is valid, or an error message array if not.
 *
 * @param value The email address to validate.
 * @returns Null if the email is valid, or an array containing an error message if it's not.
 *
 * @example
 * ```ts
 * const emailValidationResult = validEmail('test@example.com');
 * (emailValidationResult); // Outputs: ['Bitte E-Mail-Adresse im richtigen Format eingeben. Beispiel: max.mustermann@beispiel.de'] or null
 * ```
 */
export const validEmail: ValidationFunc = value => {
    const regex = /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?$/; //eslint-disable-line

    if (typeof value === 'string' && !regex.test(value)) {
        return ['Bitte E-Mail-Adresse im richtigen Format eingeben. Beispiel: max.mustermann@beispiel.de'];
    }

    return null;
};

/**
 * Validation function `validPhone` that checks if the provided phone number is valid for the 'DE' (Germany) region.
 * It uses the `parsePhoneNumberFromString` method from a phone number parsing library to interpret the phone number.
 * If the phone number is valid, the function returns null indicating no error. If it's not valid or if the value is empty,
 * it returns an error message array with a prompt to enter a valid phone number.
 *
 * @param value The phone number to validate.
 *
 * @returns     Null if the phone number is valid, or an array containing an error message if not.
 *
 * @example
 * ```ts
 * const validationResult = validPhone('1234567890');
 * (validationResult); // Outputs: ['Bitte prüfen'] or null
 * ```
 */
export const validPhone: ValidationFunc = value => {
    if (!value) return null;

    if (value) {
        const phoneNumber = parsePhoneNumberFromString(value as string, 'DE');

        if (phoneNumber?.isValid()) {
            return null;
        }
    }

    return ['Bitte validen Telefonnummer eingeben'];
};

const MAX_FILES_LIMIT = 5;
const MAX_FILE_SIZE_BYTES = 10_485_760;

/**
 * Validation function that checks if the number of uploaded files exceeds a specified limit and validates individual file sizes.
 * This function performs two key validations: first, it counts files in the form that have keys starting with 'uploadedFile-' and ensures the total doesn't exceed the maximum allowed limit of 5 files.
 * Second, it verifies that any individual file doesn't exceed the 10 MB size restriction, which helps prevent issues with file upload processing and storage constraints.
 *
 * @param value The value to validate, expected to be a File object for the file size check.
 * @param form  The form object containing all form values, used to count the number of uploaded files with keys matching the 'uploadedFile-' pattern.
 * @returns Null if both the file count and individual file size validations pass, or an array containing a descriptive error message if either validation fails.
 *
 * @example
 * ```tsx
 * // Example with too many files
 * const formWithManyFiles = {
 *   'uploadedFile-1': new File([''], 'file1.txt'),
 *   'uploadedFile-2': new File([''], 'file2.txt'),
 *   'uploadedFile-3': new File([''], 'file3.txt'),
 *   'uploadedFile-4': new File([''], 'file4.txt'),
 *   'uploadedFile-5': new File([''], 'file5.txt'),
 *   'uploadedFile-6': new File([''], 'file6.txt')
 * };
 * const result = validFileSize(null, formWithManyFiles);
 * // Returns: ['Es können maximal 5 Dateien hochgeladen werden. Bitte entfernen Sie eine Datei, um eine weitere hinzuzufügen.']
 *
 * // Example with oversized file
 * const largeFile = new File(['x'.repeat(11000000)], 'large.txt');
 * const validationResult = validFileSize(largeFile, {});
 * // Returns: ['Diese Datei ist zu groß. Bitte wählen Sie eine Datei mit einer Größe von maximal 10 MB aus.']
 *
 * // Example with valid file
 * const validFile = new File(['content'], 'small.txt');
 * const validResult = validFileSize(validFile, { 'uploadedFile-1': validFile });
 * // Returns: null
 * ```
 */
export const validFileSize: ValidationFunc = (value, form) => {
    if (Object.keys(form!).filter(key => key.startsWith('uploadedFile-')).length > MAX_FILES_LIMIT) {
        // eslint-disable-next-line max-len
        return ['Es können maximal 5 Dateien hochgeladen werden. Bitte entfernen Sie eine Datei, um eine weitere hinzuzufügen.'];
    }

    if (value instanceof File && value.size > MAX_FILE_SIZE_BYTES) {
        return ['Diese Datei ist zu groß. Bitte wählen Sie eine Datei mit einer Größe von maximal 10 MB aus.'];
    }

    return null;
};

/**
 * Creates a composite validation function from an array of individual validation functions. This utility function
 * enables combining multiple validation rules for a single form field. It iterates through each validator in the provided array
 * and returns the first encountered error message array, stopping further validation checks once an error is found.
 * This approach ensures that the user is presented with one error at a time, making it easier to address input mistakes sequentially.
 *
 * @returns A `ValidationFunc` that takes a value and an optional form object, checks the value against each validator in sequence, and returns the first non-null result as an error message array. If no validators return an error, `null` is returned, indicating that the value passed all validation checks.
 *
 * @example
 * ```tsx
 * const validator = merge([required, minMaxLength(5, 15)]);
 * const errors = validator('short', {}); // Might return an error from the `minMaxLength` validator
 * const noErrors = validator('valid length', {}); // Returns null, indicating no validation errors
 * ```
 */
const MemoizedMerge = () => {
    const cache = new Map<ValidationFunc[], ValidationFunc>();

    return (validators: ValidationFunc[]): ValidationFunc => {
        if (cache.has(validators)) {
            return cache.get(validators)!;
        }

        return (value, form) => {
            // eslint-disable-next-line no-restricted-syntax
            for (const validator of validators) {
                const result = validator(value, form);

                if (Array.isArray(result)) {
                    return result;
                }
            }

            return null;
        };
    };
};

/**
 * Creates a composite validation function from an array of individual validation functions. This utility function
 * enables combining multiple validation rules for a single form field. It iterates through each validator in the provided array
 * and returns the first encountered error message array, stopping further validation checks once an error is found.
 * This approach ensures that the user is presented with one error at a time, making it easier to address input mistakes sequentially.
 *
 * @param validators An array of `ValidationFunc` functions that are to be combined into a single validation function.
 * @returns A `ValidationFunc` that takes a value and an optional form object, checks the value against each validator in sequence, and returns the first non-null result as an error message array. If no validators return an error, `null` is returned, indicating that the value passed all validation checks.
 *
 * @example
 * ```tsx
 * const validator = merge([required, minMaxLength(5, 15)]);
 * const errors = validator('short', {}); // Might return an error from the `minMaxLength` validator
 * const noErrors = validator('valid length', {}); // Returns null, indicating no validation errors
 * ```
 */
export const merge = MemoizedMerge();